import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Search, Trash2, Users } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
} from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { modules } from "../lib/modules";
import styles from "./users.module.css";

type AdminUser = {
  id: number;
  username: string;
  display_name?: string | null;
  role: string;
  modules: string[];
  active: boolean;
  created_at: string;
  updated_at?: string | null;
};

type UserDraft = {
  username: string;
  display_name: string;
  password: string;
  role: string;
  modules: string[];
  active: boolean;
};
const emptyDraft: UserDraft = {
  username: "",
  display_name: "",
  password: "",
  role: "user",
  modules: [],
  active: true,
};
const meetingRights = [
  {
    id: "transcription-analysis-config-dev",
    title: "Редактировать настройки и промпты встреч DEV",
  },
  {
    id: "transcription-analysis-internal",
    title: "Все встречи, внутренние итоги и внутренний промпт",
  },
  {
    id: "transcription-analysis-followup-prompt",
    title: "Настройка клиентского промпта",
  },
  {
    id: "transcription-analysis-config-prod",
    title: "Редактировать настройки и промпты встреч PROD",
  },
] as const;
const configRights = [
  { id: "maia-config-dev", title: "Редактировать настройки и промпты DEV" },
  { id: "maia-config-prod", title: "Редактировать настройки и промпты PROD" },
] as const;
const auxiliaryRights = new Set([...meetingRights, ...configRights].map((right) => right.id));

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const canManage = (target: AdminUser) => currentUser?.role === "admin" ||
    (target.role !== "admin" && target.modules.every((right) => currentUser?.modules?.includes(right)));
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<AdminUser | null | undefined>(
    undefined,
  );
  const [draft, setDraft] = useState<UserDraft>(emptyDraft);
  const [error, setError] = useState("");
  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: () => api<AdminUser[]>("/api/users"),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      api<AdminUser>(editing ? `/api/users/${editing.id}` : "/api/users", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify({
          ...draft,
          display_name: draft.display_name || null,
          password: draft.password || undefined,
        }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      close();
    },
    onError: (caught) =>
      setError(
        caught instanceof Error
          ? caught.message
          : "Не удалось сохранить пользователя",
      ),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api(`/api/users/${id}`, { method: "DELETE" }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("ru");
    return (usersQuery.data || []).filter(
      (user) =>
        !query ||
        `${user.username} ${user.display_name || ""}`
          .toLocaleLowerCase("ru")
          .includes(query),
    );
  }, [search, usersQuery.data]);

  function open(user?: AdminUser) {
    setEditing(user || null);
    setDraft(
      user
        ? {
            username: user.username,
            display_name: user.display_name || "",
            password: "",
            role: user.role,
            modules: user.modules,
            active: user.active,
          }
        : emptyDraft,
    );
    setError("");
  }
  function close() {
    setEditing(undefined);
    setError("");
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    saveMutation.mutate();
  }
  function toggleModule(id: string) {
    setDraft((value) => ({
      ...value,
      modules: value.modules.includes(id)
        ? value.modules.filter(
            (item) =>
              item !== id &&
              (id !== "transcription-analysis" || !meetingRights.some((right) => right.id === item)) &&
              (id !== "maia" || !configRights.some((right) => right.id === item)),
          )
        : [...value.modules, id],
    }));
  }

  function toggleMeetingRight(id: (typeof meetingRights)[number]["id"]) {
    setDraft((value) => {
      const enabled = value.modules.includes(id);
      return {
        ...value,
        modules: enabled
          ? value.modules.filter((item) => item !== id)
          : Array.from(
              new Set([...value.modules, "transcription-analysis", id]),
            ),
      };
    });
  }

  function toggleConfigRight(id: (typeof configRights)[number]["id"]) {
    setDraft((value) => ({
      ...value,
      modules: value.modules.includes(id)
        ? value.modules.filter((item) => item !== id)
        : Array.from(new Set([...value.modules, "maia", id])),
    }));
  }

  return (
    <main className={styles.page}>
      <PageHeader
        title="Пользователи"
        description="Общие учётные записи DEV и PROD. Права редактирования каждого окружения выдаются отдельно."
        actions={
          <Button variant="primary" onClick={() => open()}>
            <Plus size={15} />
            Добавить
          </Button>
        }
      />
      <section className={styles.panel}>
        <div className={styles.toolbar}>
          <div className={styles.search}>
            <Search size={15} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Найти пользователя"
            />
          </div>
          <span>{filtered.length} пользователей</span>
        </div>
        {usersQuery.isLoading ? (
          <div className={styles.loading}>Загрузка пользователей</div>
        ) : usersQuery.isError ? (
          <EmptyState
            icon={<Users size={28} />}
            title="Не удалось загрузить пользователей"
            description={(usersQuery.error as Error).message}
            action={
              <Button onClick={() => void usersQuery.refetch()}>
                Повторить
              </Button>
            }
          />
        ) : filtered.length ? (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Пользователь</th>
                  <th>Роль</th>
                  <th>Доступ</th>
                  <th>Статус</th>
                  <th>Создан</th>
                  <th aria-label="Действия" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className={styles.identity}>
                        <span>
                          {(user.display_name || user.username)
                            .slice(0, 1)
                            .toUpperCase()}
                        </span>
                        <div>
                          <strong>{user.display_name || user.username}</strong>
                          {user.display_name ? (
                            <small>{user.username}</small>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge>
                        {user.role === "admin"
                          ? "Администратор"
                          : "Пользователь"}
                      </Badge>
                    </td>
                    <td className={styles.muted}>
                      {user.role === "admin"
                        ? "Все модули"
                        : `${user.modules.filter((item) => !auxiliaryRights.has(item as (typeof meetingRights)[number]["id"])).length} модулей`}
                    </td>
                    <td>
                      <Badge tone={user.active ? "success" : "neutral"}>
                        {user.active ? "Активен" : "Отключён"}
                      </Badge>
                    </td>
                    <td className={styles.muted}>
                      {new Date(user.created_at).toLocaleDateString("ru-RU")}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          onClick={() => open(user)}
                          aria-label="Изменить"
                          disabled={!canManage(user)}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => {
                            if (
                              confirm(`Удалить пользователя ${user.username}?`)
                            )
                              deleteMutation.mutate(user.id);
                          }}
                          aria-label="Удалить"
                          disabled={!canManage(user)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<Users size={28} />}
            title="Пользователи не найдены"
            description="Измените поисковый запрос или добавьте новую учётную запись."
          />
        )}
      </section>

      <Modal
        open={editing !== undefined}
        onOpenChange={(value) => {
          if (!value) close();
        }}
        title={editing ? "Редактировать пользователя" : "Новый пользователь"}
        description="Основные данные и доступ к модулям."
        footer={
          <>
            <Button onClick={close}>Отмена</Button>
            <Button
              variant="primary"
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Сохранить
            </Button>
          </>
        }
      >
        <form className={styles.form} onSubmit={submit}>
          <div className={styles.twoColumns}>
            <Field label="Логин">
              <Input
                required
                value={draft.username}
                onChange={(event) =>
                  setDraft({ ...draft, username: event.target.value })
                }
              />
            </Field>
            <Field label="Отображаемое имя">
              <Input
                value={draft.display_name}
                onChange={(event) =>
                  setDraft({ ...draft, display_name: event.target.value })
                }
              />
            </Field>
          </div>
          <div className={styles.twoColumns}>
            <Field
              label={editing ? "Новый пароль" : "Пароль"}
              hint={editing ? "Оставьте пустым, чтобы не менять" : undefined}
            >
              <Input
                required={!editing}
                type="password"
                value={draft.password}
                onChange={(event) =>
                  setDraft({ ...draft, password: event.target.value })
                }
              />
            </Field>
            <Field label="Роль">
              <select
                className={styles.select}
                value={draft.role}
                disabled={currentUser?.role !== "admin"}
                onChange={(event) =>
                  setDraft({ ...draft, role: event.target.value })
                }
              >
                <option value="user">Пользователь</option>
                <option value="admin">Администратор</option>
              </select>
            </Field>
          </div>
          <div>
            <p className={styles.formLabel}>Доступ к модулям</p>
            <div className={styles.moduleGrid}>
              {modules.map((item) => (
                <div key={item.id} className={styles.moduleBlock}>
                  <label className={styles.moduleOption}>
                    <input
                      type="checkbox"
                      checked={
                        draft.role === "admin" ||
                        draft.modules.includes(item.id)
                      }
                      disabled={draft.role === "admin"}
                      onChange={() => toggleModule(item.id)}
                    />
                    <span>
                      <strong>{item.title}</strong>
                      <small>{item.description}</small>
                    </span>
                  </label>
                  {item.id === "transcription-analysis" ? (
                    <div className={styles.subrights}>
                      {meetingRights.map((right) => (
                        <label key={right.id}>
                          <input
                            type="checkbox"
                            checked={
                              draft.role === "admin" ||
                              draft.modules.includes(right.id)
                            }
                            disabled={
                              draft.role === "admin" ||
                              !draft.modules.includes("transcription-analysis")
                            }
                            onChange={() => toggleMeetingRight(right.id)}
                          />
                          <span>{right.title}</span>
                        </label>
                      ))}
                    </div>
                  ) : null}
                  {item.id === "maia" ? (
                    <div className={styles.subrights}>
                      {configRights.map((right) => (
                        <label key={right.id}>
                          <input type="checkbox"
                            checked={draft.role === "admin" || draft.modules.includes(right.id)}
                            disabled={draft.role === "admin" || !draft.modules.includes("maia")}
                            onChange={() => toggleConfigRight(right.id)} />
                          <span>{right.title}</span>
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <label className={styles.activeToggle}>
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(event) =>
                setDraft({ ...draft, active: event.target.checked })
              }
            />
            Учётная запись активна
          </label>
          {error ? <p className={styles.formError}>{error}</p> : null}
          <button hidden type="submit" />
        </form>
      </Modal>
    </main>
  );
}
